import styled from 'styled-components';
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PROFILE } from './lang';
import { Alert, handleMapping, ROLES_MODULE, GS_ROLES_MODULE } from '../';
import { ROLES_SWITCH_BAR_TEXT } from '../RoleSwitchBar/lang';
import {
  useGetApplicantNameByApplicationId,
  useGetApplicantNameByAuthEmail,
} from '../../pages';
import { useAuth } from '../../contexts';

type UserPanelProps = {
  hidden: boolean;
  isStaffViewApplicant: boolean;
};

const useApplicationId = (): string => {
  const location = useLocation();
  const path = location.pathname;
  const match = path.match(/^\/application\/([a-zA-Z0-9]+)$/);
  return match ? match[1] : '';
};

const useApplicantId = (): string => {
  const location = useLocation();
  const path = location.pathname;
  const match = path.match(/^\/applicant\/([a-zA-Z0-9]+)$/);
  return match ? match[1] : '';
};

export const applicantPage = (): boolean => {
  const applicantPage = [
    /^\/application\/([a-zA-Z0-9]+)$/, // to match the dynamic path /application/:applicationId
    /^\/applicant\/([a-zA-Z0-9]+)$/,
  ];

  return applicantPage.some(route =>
    typeof route === 'string'
      ? route === window.location.pathname
      : route.test(window.location.pathname),
  );
};

export const UserPanel: React.FunctionComponent<UserPanelProps> = (
  props: UserPanelProps,
) => {
  const { hidden, isStaffViewApplicant } = props;
  const applicationId = useApplicationId();
  const applicantId = useApplicantId();

  const navigate = useNavigate();
  const [currentRole, setCurrentRole] = useState<ROLES_MODULE>(ROLES_MODULE.NA);

  const { user, role } = useAuth();

  const dept = user?.userInfo.department ?? '';

  const isNotApplicant: boolean = user?.role[0] !== 'applicant';
  const isGSMember = Object.values(GS_ROLES_MODULE).includes(
    role as GS_ROLES_MODULE,
  );

  const isARMember = role === ROLES_MODULE.RIGHT_AR;

  useEffect(() => {
    if (typeof role === 'string') setCurrentRole(role as ROLES_MODULE);
  }, [role]);

  // for staff
  // should use the applicationId in path to get the information of the applicant
  // instead of using auth
  const {
    applicantInfo,
    loading,
    error: applicantInfoError,
  } = useGetApplicantNameByApplicationId(
    applicationId,
    applicantId,
    !isNotApplicant,
  );

  // for applicant self
  // eg. /applications
  const {
    applicantInfo: applicantName,
    loading: applicantNameLoading,
    error: applicantNameError,
  } = useGetApplicantNameByAuthEmail(
    user?.userInfo?.username ?? '',
    isNotApplicant,
  );

  if (applicantInfoError ?? applicantNameError) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent:
            `Error Occurs.` +
            (applicantInfoError as Error)?.message +
            (applicantNameError as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  const handleViewInfo = (): void => {
    if (!isGSMember && !isARMember && isNotApplicant) return;
    const applicantId = isNotApplicant
      ? (applicantInfo?.applicantId ?? '')
      : (applicantName?.applicantId ?? '');
    if (applicantId) navigate(`/applicant/${applicantId}`);
  };

  return (
    user &&
    !hidden && (
      <UserPanelContainer>
        {!isStaffViewApplicant ? (
          <>
            {!applicantNameLoading && !isNotApplicant && (
              <UserPanelItem className="whitespace-nowrap">
                <div className="font-bold">{PROFILE.NAME}</div>
                <div
                  className="cursor-pointer hover:underline"
                  onClick={handleViewInfo}
                >
                  {(applicantName?.givenName ?? applicantName?.surname)
                    ? `${applicantName?.surname?.toUpperCase()} ${applicantName?.givenName}`
                    : 'N/A'}
                </div>
              </UserPanelItem>
            )}

            {isNotApplicant ? (
              <UserPanelItem className="whitespace-nowrap">
                <div className="font-bold">{PROFILE.NAME}</div>
                <div>
                  {user?.userInfo?.displayName ?? 'N/A'} {dept}
                </div>
              </UserPanelItem>
            ) : (
              <UserPanelItem className="whitespace-nowrap">
                <div className="font-bold">{PROFILE.EMAIL_ACCOUNT}</div>
                <div>{user?.userInfo?.username ?? 'N/A'}</div>
              </UserPanelItem>
            )}

            {isNotApplicant && typeof role === 'string' && (
              <UserPanelItem className="whitespace-nowrap mx-2">
                <div className="font-bold">{PROFILE.CURRENT_ROLE}</div>
                <span>
                  {ROLES_SWITCH_BAR_TEXT.NAMING[handleMapping(currentRole)] ??
                    'N/A'}
                </span>
              </UserPanelItem>
            )}
          </>
        ) : (
          <>
            {!loading && (
              <UserPanelItem className="whitespace-nowrap">
                <div>
                  View As&nbsp;
                  <b
                    onClick={handleViewInfo}
                    className={
                      isGSMember || isARMember
                        ? 'underline underline-offset-4 cursor-pointer'
                        : ''
                    }
                  >
                    {applicantInfo?.surname}&nbsp;{applicantInfo?.givenName}
                    &nbsp;
                    {applicantInfo?.applicationNo === undefined
                      ? ''
                      : '(No.' + applicantInfo?.applicationNo + ')'}
                  </b>
                </div>
              </UserPanelItem>
            )}
          </>
        )}
      </UserPanelContainer>
    )
  );
};

const UserPanelContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const UserPanelItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  div {
    margin-right: 8px;
  }
`;
